import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar } from 'recharts';
import { adminApi } from '../lib/api';

const COLORS = ['#0A1F44', '#3B82F6', '#64748B', '#334155', '#2563EB', '#0e2a5c', '#8B4513', '#2d3748'];

function Kpi({ label, value, change, testid }) {
  const up = typeof change === 'number' && change >= 0;
  return (
    <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#64748B]">{label}</div>
      <div className="font-display text-3xl font-bold text-[#0A1F44] mt-2">{value}</div>
      {typeof change === 'number' && (
        <div className={`mt-2 text-xs flex items-center gap-1 ${up ? 'text-green-600' : 'text-red-600'}`}>
          {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {up ? '+' : ''}{change}%
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { adminApi.get('/admin/stats').then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-[#64748B]">Laster…</div>;
  const { kpis, daily, by_category, top_treatments, top_clinics, recent } = data;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-3xl font-bold text-[#0A1F44]">Oversikt</h1>
        <p className="text-[#64748B] mt-1 text-sm">Siste 30 dager</p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Totale henvendelser" value={kpis.total_leads} change={kpis.total_leads_change} testid="kpi-total" />
        <Kpi label="Nye i dag" value={kpis.new_today} change={kpis.new_today_change} testid="kpi-today" />
        <Kpi label="Sidebesøk 30d" value={kpis.sessions_30d} change={kpis.sessions_change} testid="kpi-sessions" />
        <Kpi label="Konvertering" value={`${kpis.conversion_rate}%`} testid="kpi-conversion" />
      </div>

      <div className="grid lg:grid-cols-[3fr_2fr] gap-5">
        <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
          <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Daglig aktivitet</h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={daily}>
              <XAxis dataKey="date" hide />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip />
              <Line type="monotone" dataKey="leads" stroke="#3B82F6" strokeWidth={2.5} dot={false} name="Henvendelser" />
              <Line type="monotone" dataKey="views" stroke="#0A1F44" strokeWidth={2} dot={false} name="Sidevisninger" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
          <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Henvendelser per kategori</h2>
          {by_category.length === 0 ? <div className="text-sm text-[#64748B]">Ingen data ennå</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={by_category} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
                  {by_category.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                </Pie>
                <Tooltip />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
          <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Topp 5 behandlinger</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={top_treatments} layout="vertical">
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" width={140} stroke="#0A1F44" fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10">
          <h2 className="font-display text-lg font-bold text-[#0A1F44] mb-4">Topp 5 klinikker</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={top_clinics} layout="vertical">
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" width={140} stroke="#0A1F44" fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#0A1F44" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#0A1F44]/10 overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-[#0A1F44]">Siste henvendelser</h2>
          <Link to="/admin/henvendelser" className="text-sm text-[#3B82F6] font-semibold flex items-center gap-1">Se alle <ArrowRight size={14}/></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B] bg-[#FAF8F5]">
                <th className="px-5 py-3">Tid</th><th>Navn</th><th>Behandling</th><th>Klinikk</th><th>By</th><th className="pr-5">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-[#64748B] text-center">Ingen henvendelser ennå</td></tr>}
              {recent.map((l) => (
                <tr key={l.id} className="border-t border-[#0A1F44]/5">
                  <td className="px-5 py-3 text-[#64748B]">{new Date(l.created_at).toLocaleString('no-NO')}</td>
                  <td className="font-semibold text-[#0A1F44]">{l.name}</td>
                  <td>{l.treatment}</td>
                  <td>{l.clinic_selected || '—'}</td>
                  <td>{l.city || '—'}</td>
                  <td className="pr-5">
                    <StatusBadge status={l.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: 'bg-[#3B82F6]/10 text-[#3B82F6]',
    sent: 'bg-blue-100 text-blue-700',
    converted: 'bg-green-100 text-green-700',
    invalid: 'bg-gray-200 text-gray-600',
  };
  const labels = { new: 'Ny', sent: 'Sendt', converted: 'Konvertert', invalid: 'Ugyldig' };
  return <span className={`inline-block px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-full ${map[status] || map.new}`}>{labels[status] || status}</span>;
}
